import React, { useState } from 'react';
import { Layout } from '../../components/Layout';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { CallbackModal } from '../../components/CallbackModal';
import { Calendar, Clock, Tag, ChevronLeft, MessageCircle, Plus, Minus, AlertCircle, Phone } from 'lucide-react';
import { Link } from 'react-router-dom';
const BOOKING_URL = 'https://denticoclinic.patientportal.onl/bookings';
const WHATSAPP_URL = 'https://wa.me/447345197820';
function AccordionItem({
  question,
  answer,
  isOpen,
  onToggle
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onToggle: () => void;
}) {
  return <div className="border-b border-gray-100 last:border-b-0" data-id="element-846">
      <button onClick={onToggle} className="w-full flex items-center justify-between py-4 text-left focus:outline-none group" data-id="element-847">
        <span className="text-lg font-medium text-gray-900 group-hover:text-teal-600 transition-colors" data-id="element-848">
          {question}
        </span>
        <span className={`flex-shrink-0 ml-4 w-6 h-6 rounded-full flex items-center justify-center transition-colors ${isOpen ? 'bg-teal-600 text-white' : 'bg-gray-100 text-gray-400'}`} data-id="element-849">
          {isOpen ? <Minus className="w-4 h-4" data-id="element-850" /> : <Plus className="w-4 h-4" data-id="element-851" />}
        </span>
      </button>
      <div className={`overflow-hidden transition-all duration-300 ease-in-out ${isOpen ? 'max-h-96 opacity-100 pb-4' : 'max-h-0 opacity-0'}`} data-id="element-852">
        <p className="text-gray-600 leading-relaxed" data-id="element-853">{answer}</p>
      </div>
    </div>;
}
export function ToothPainArticle() {
  const [isCallbackOpen, setIsCallbackOpen] = useState(false);
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  const toggleFaq = (index: number) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };
  const faqs = [{
    q: 'Що робити, якщо зубний біль нестерпний?',
    a: 'Прийміть знеболююче (ібупрофен або парацетамол) і негайно зверніться до стоматолога. Якщо є набряк, утруднене дихання або ковтання — телефонуйте 111 або їдьте в A&E.'
  }, {
    q: 'Чи можна гріти хворий зуб?',
    a: 'Ні! Тепло може посилити запалення і набряк. Краще прикладіть холодний компрес до щоки.'
  }, {
    q: 'Як знайти екстреного стоматолога в Лондоні?',
    a: 'Ви можете зателефонувати в NHS 111 для поради або знайти приватну клініку, яка пропонує "Emergency Dentist" послуги. Dentico Clinic приймає термінових пацієнтів.'
  }, {
    q: 'Скільки коштує екстрений візит?',
    a: 'У приватних клініках це зазвичай £50-£100 за консультацію плюс вартість лікування. В NHS це £26.80 (Band 1), але знайти вільний слот важко.'
  }, {
    q: 'Що робити, якщо випала пломба?',
    a: 'Намагайтеся не жувати на цей бік, тримайте зуб у чистоті і запишіться до лікаря. Можна використати тимчасовий цемент з аптеки як тимчасове рішення.'
  }];
  // Schemas
  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: 'Що робити при зубному болю в Лондоні? Покрокова інструкція',
    datePublished: '2026-02-12',
    author: {
      '@type': 'Organization',
      name: 'Dentico Clinic'
    },
    publisher: {
      '@type': 'Organization',
      name: 'Dentico Clinic',
      logo: {
        '@type': 'ImageObject',
        url: 'https://denticoclinic.co.uk/logo.png'
      }
    },
    description: 'Інструкція дій при гострому зубному болю в Лондоні. Куди дзвонити, перша допомога та як знайти лікаря.',
    inLanguage: 'uk'
  };
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map(faq => ({
      '@type': 'Question',
      name: faq.q,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.a
      }
    }))
  };
  return <Layout data-id="element-854">
      <CallbackModal isOpen={isCallbackOpen} onClose={() => setIsCallbackOpen(false)} data-id="element-855" />

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(articleSchema)
    }} data-id="element-856" />
      <script type="application/ld+json" dangerouslySetInnerHTML={{
      __html: JSON.stringify(faqSchema)
    }} data-id="element-857" />

      <article className="bg-white" data-id="element-858">
        {/* Header */}
        <div className="bg-red-50 py-12 lg:py-20" data-id="element-859">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-860">
            <Link to="/ua/blog" className="inline-flex items-center text-red-600 font-medium mb-6 hover:underline" data-id="element-861">
              <ChevronLeft className="w-4 h-4 mr-1" data-id="element-862" />
              Назад до блогу
            </Link>

            <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-600" data-id="element-863">
              <span className="bg-white px-3 py-1 rounded-full font-medium text-red-700 flex items-center shadow-sm" data-id="element-864">
                <Tag className="w-3 h-3 mr-2" data-id="element-865" />
                Невідкладна допомога
              </span>
              <span className="flex items-center" data-id="element-866">
                <Calendar className="w-4 h-4 mr-2" data-id="element-867" />
                Лютий 2026
              </span>
              <span className="flex items-center" data-id="element-868">
                <Clock className="w-4 h-4 mr-2" data-id="element-869" />6 хв читання
              </span>
            </div>

            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6" data-id="element-870">
              Що робити при зубному болю в Лондоні? Покрокова інструкція
            </h1>

            <div className="flex items-center" data-id="element-871">
              <div className="w-10 h-10 bg-red-200 rounded-full flex items-center justify-center text-red-700 font-bold mr-3" data-id="element-872">
                DC
              </div>
              <div data-id="element-873">
                <p className="font-medium text-gray-900" data-id="element-874">Dentico Clinic Team</p>
                <p className="text-sm text-gray-500" data-id="element-875">Експертна стаття</p>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-876">
          <div className="prose prose-lg prose-red max-w-none text-gray-600" data-id="element-877">
            <p className="lead text-xl text-gray-800 font-medium mb-8" data-id="element-878">
              Зубний біль завжди приходить невчасно. Якщо ви в Лондоні і у вас
              розболівся зуб, важливо діяти швидко і правильно. Ось покрокова
              інструкція, що робити, куди дзвонити і як отримати допомогу.
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-879">
              Крок 1: Перша допомога вдома
            </h2>
            <p className="mb-6" data-id="element-880">
              Поки ви шукаєте лікаря, спробуйте полегшити стан:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-8" data-id="element-881">
              <li data-id="element-882">
                <strong data-id="element-883">Знеболюючі:</strong> Прийміть ібупрофен або парацетамол
                (якщо немає протипоказань). Дотримуйтесь дозування!
              </li>
              <li data-id="element-884">
                <strong data-id="element-885">Полоскання:</strong> Прополощіть рот теплою підсоленою
                водою (чайна ложка солі на склянку води). Це допоможе зняти
                запалення.
              </li>
              <li data-id="element-886">
                <strong data-id="element-887">Холод:</strong> Прикладіть холодний компрес до щоки з
                боку хворого зуба. Це зменшить набряк.
              </li>
              <li data-id="element-888">
                <strong data-id="element-889">Сон:</strong> Спіть з піднятою головою (на високій
                подушці), щоб зменшити приплив крові до голови і біль.
              </li>
            </ul>

            <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-8" data-id="element-890">
              <h3 className="font-bold text-red-800 mb-2 flex items-center" data-id="element-891">
                <AlertCircle className="w-5 h-5 mr-2" data-id="element-892" />
                Що НЕ МОЖНА робити:
              </h3>
              <ul className="list-disc pl-6 text-red-700 space-y-1" data-id="element-893">
                <li data-id="element-894">
                  Не грійте хворе місце (грілкою, шарфом). Тепло посилює
                  інфекцію!
                </li>
                <li data-id="element-895">
                  Не кладіть таблетку аспірину на ясна — це викличе хімічний
                  опік.
                </li>
                <li data-id="element-896">
                  Не ігноруйте біль, навіть якщо він вщух. Проблема не зникла.
                </li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-897">
              Крок 2: Оцініть ситуацію
            </h2>
            <p className="mb-6" data-id="element-898">
              Вам потрібна <strong data-id="element-899">невідкладна допомога (Emergency)</strong>,
              якщо:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-8" data-id="element-900">
              <li data-id="element-901">Біль нестерпний і не знімається таблетками.</li>
              <li data-id="element-902">Є набряк обличчя, шиї або ясен.</li>
              <li data-id="element-903">Вам важко ковтати або дихати (це небезпечно!).</li>
              <li data-id="element-904">Була травма (вибитий зуб, удар).</li>
              <li data-id="element-905">Кровотеча не зупиняється.</li>
            </ul>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-906">
              Крок 3: Куди звернутися?
            </h2>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-4" data-id="element-907">
              Варіант А: Приватна клініка (Найшвидше)
            </h3>
            <p className="mb-4" data-id="element-908">
              Більшість приватних клінік мають слоти для "Emergency Dentist". Це
              найшвидший спосіб отримати допомогу.
            </p>
            <p className="mb-6" data-id="element-909">
              У <strong data-id="element-910">Dentico Clinic</strong> ми завжди намагаємося прийняти
              пацієнтів з гострим болем у той же день. Ви можете написати нам у
              WhatsApp навіть у неробочий час — ми відповімо, як тільки зможемо.
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-4" data-id="element-911">
              Варіант Б: NHS 111
            </h3>
            <p className="mb-4" data-id="element-912">
              Якщо ви не можете дозволити собі приватного лікаря, зателефонуйте
              за номером <strong data-id="element-913">111</strong>. Оператор оцінить ваші симптоми і
              підкаже найближчу чергову NHS клініку.
            </p>
            <p className="mb-6" data-id="element-914">
              <em data-id="element-915">Увага: Знайти вільний NHS слот день-у-день дуже складно.</em>
            </p>

            <h3 className="text-xl font-bold text-gray-900 mt-6 mb-4" data-id="element-916">
              Варіант В: A&E (Лікарня)
            </h3>
            <p className="mb-6" data-id="element-917">
              Їдьте в A&E (Accident & Emergency) ТІЛЬКИ якщо:
            </p>
            <ul className="list-disc pl-6 space-y-2 mb-8" data-id="element-918">
              <li data-id="element-919">У вас сильна кровотеча.</li>
              <li data-id="element-920">Набряк заважає дихати або ковтати.</li>
              <li data-id="element-921">Була серйозна травма щелепи.</li>
            </ul>
            <p className="mb-8" data-id="element-922">
              <em data-id="element-923">Звичайний зубний біль в лікарні не лікують!</em>
            </p>

            <h2 className="text-2xl font-bold text-gray-900 mt-10 mb-6" data-id="element-924">
              Як Dentico Clinic може допомогти?
            </h2>
            <p className="mb-6" data-id="element-925">
              Ми розуміємо, як страшно мати зубний біль у чужій країні. У нас:
            </p>
            <ul className="space-y-3 mb-8" data-id="element-926">
              <li className="flex items-start" data-id="element-927">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-928" />
                <span data-id="element-929">Ми говоримо українською.</span>
              </li>
              <li className="flex items-start" data-id="element-930">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-931" />
                <span data-id="element-932">Ми приймаємо терміново.</span>
              </li>
              <li className="flex items-start" data-id="element-933">
                <CheckCircle2 className="w-5 h-5 text-teal-600 mr-3 mt-1 flex-shrink-0" data-id="element-934" />
                <span data-id="element-935">Ми зробимо все, щоб врятувати зуб.</span>
              </li>
            </ul>
          </div>

          {/* FAQ Section */}
          <div className="mt-16 pt-10 border-t border-gray-100" data-id="element-936">
            <h3 className="text-2xl font-bold text-gray-900 mb-8" data-id="element-937">
              Часті запитання
            </h3>
            <div className="space-y-2" data-id="element-938">
              {faqs.map((faq, idx) => <AccordionItem key={idx} question={faq.q} answer={faq.a} isOpen={openFaqIndex === idx} onToggle={() => toggleFaq(idx)} data-id="element-939" />)}
            </div>
          </div>

          {/* Read Also */}
          <div className="mt-16" data-id="element-940">
            <h3 className="text-2xl font-bold text-gray-900 mb-6" data-id="element-941">
              Читайте також
            </h3>
            <div className="grid sm:grid-cols-2 gap-4" data-id="element-942">
              <Link to="/ua/emergency-dentist-london" data-id="element-943">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-944">
                  <span className="text-teal-600 font-medium" data-id="element-945">
                    Невідкладна допомога в Dentico
                  </span>
                </Card>
              </Link>
              <Link to="/ua/root-canal-london" data-id="element-946">
                <Card className="p-4 hover:shadow-md transition-shadow h-full flex items-center" data-id="element-947">
                  <span className="text-teal-600 font-medium" data-id="element-948">
                    Лікування каналів
                  </span>
                </Card>
              </Link>
            </div>
          </div>
        </div>

        {/* CTA */}
        <section className="py-16 bg-red-900" data-id="element-949">
          <div className="max-w-4xl mx-auto px-4 text-center" data-id="element-950">
            <h2 className="text-3xl font-bold text-white mb-4" data-id="element-951">
              Потрібна термінова допомога?
            </h2>
            <p className="text-xl text-red-100 mb-8 max-w-2xl mx-auto" data-id="element-952">
              Не чекайте. Зв'яжіться з нами зараз.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-953">
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto" data-id="element-954">
                <Button size="lg" className="w-full gap-2 bg-green-600 hover:bg-green-700 text-white border-none" data-id="element-955">
                  <MessageCircle className="w-5 h-5" data-id="element-956" />
                  Написати в WhatsApp
                </Button>
              </a>
              <a href="tel:+447345197820" className="w-full sm:w-auto" data-id="element-957">
                <Button variant="outline" size="lg" className="w-full gap-2 border-white text-white hover:bg-white/10" data-id="element-958">
                  <Phone className="w-5 h-5" data-id="element-959" />
                  +44 7345 197820
                </Button>
              </a>
            </div>
          </div>
        </section>
      </article>
    </Layout>;
}